import { isPlatformServer } from '@angular/common';
import { ChangeDetectorRef, Component, effect, OnInit, OnDestroy, PLATFORM_ID, inject, isDevMode, DOCUMENT, EnvironmentInjector, runInInjectionContext, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
  ActivatedRoute,
} from '@angular/router';
import { User } from '@angular/fire/auth';
import { HeaderComponent } from '../header.component';
import { IAction, IDisplayMode } from '../interfaces';
import { QUERY_PARAMS } from '../constants/query-params';
import {
  AuctionService,
  AuthService,
  LoadingService,
  NotificationService,
  PathService,
  RegistrationIntentService,
  UserService,
} from '../services';
import { CookieService } from '../services/cookie.service';
import { consumeSignalEvents } from '../services/signal-event';
import { FirebaseService } from '../services/firebase.service';
import { NotificationsComponent } from './notifications.component';
import { LoginComponent } from '../users/login.component';

declare const window: any;
@Component({
    imports: [
    RouterModule,
    HeaderComponent,
    NotificationsComponent,
    LoginComponent,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatToolbarModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    PopoverModule,
    SelectModule,
],
    selector: 'app-topbar-component',
    styleUrls: ['./topbar.component.scss'],
    templateUrl: `./topbar.component.html`
})
export class TopbarComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  public readonly isAuthenticated = this.authService.isAuthenticated;
  public readonly authResolved = this.authService.resolved;
  private hostname = '';
  /** Ask if we can present the browser notification UI. */
  public showBrowserNotificationAd = false;
  public uid = '';
  public offline = false;
  public loading = false;
  public user!: User;
  public loaded = false;
  public auctionPath = /^\/auctions\/(.*?)(?:\/.*)*$/;
  public onAuctionAddPage = false;
  public header = '';

  public loadingRoute = false;
  public onlyAdminsViewAuctionButton = false;
  public currentAuction = '';
  public filtering = false;
  public firstName = signal('Profile');
  public get sid() { return this.pathService?.model?.sid || ''; }
  public IDisplayMode = IDisplayMode;
  public createPage = '';
  public loginPage = '';
  public loginPageUtms = '';
  public userEditDialog!: MatDialogRef<any>;
  public userData: any;
  public headerSearchTerm = '';
  public readonly enableBackGroundBlur = true;
  public loginDialogVisible = false;
  public loginDialogPathname: string | null = null;
  private showHomeSites = [
    'bid_jlrauctions_appspot_com',
    'rasmus_auctions_appspot_com',
    'superb_ascent_appspot_com',
    'cal_auctions_appspot_com',
    'danielauction',
    'dubois',
  ];
  public showHome = false;
  /** Categories shown in the subheader dropdown. */
  public categoryOptions: Array<{ label: string; value: string }> = [];
  /** Currently selected category from the subheader dropdown. */
  public selectedCategory: string | null = null;
  /** Style class applied to the category dropdown panel. */
  public readonly selectPanelClass = 'subheader-select-panel';
  /** Center links displayed in the subheader. */
  public readonly centerNavLinks: Array<{ label: string; routerLink?: string; href?: string }> = [
    { label: 'Rasmus TV', routerLink: '/rasmus-tv' },
    { label: 'Sell Real Estate', routerLink: '/real-estate' },
    { label: 'Sell on Rasmus', routerLink: '/sellers' },
  ];
  /** Right-side links displayed in the subheader. */
  public readonly rightNavLinks: Array<{ label: string; routerLink?: string; href?: string }> = [
    { label: 'Support', routerLink: '/knowledge-base' },
  ];
  /** Links displayed inside the "More" menu. */
  public readonly moreMenuLinks: Array<{ label: string; routerLink?: string; href?: string }> = [
    { label: 'Buyers', routerLink: '/buyers' },
    { label: 'Get Alerts', routerLink: '/sms-signup' },
    { label: 'Help', routerLink: '/knowledge-base' },
    { label: 'Contact Us', routerLink: '/contact' },
    { label: 'About Us', routerLink: '/about-us' },
    { label: 'Blog', routerLink: '/blog' },
    { label: 'For Sellers', routerLink: '/for-sellers' },
  ];
  public utmParams: { [key: string]: string } = {};
  public document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID) as string;
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly signalEventEffectsReady = signal(false);
  private syncedHeaderUserUid = '';
  private loadingUpdateToken = 0;

  private runInContext<T>(operation: () => T): T {
    return runInInjectionContext(this.environmentInjector, operation);
  }

  /**
   * Returns the active pathname from the browser when available, falling back to the router URL.
   */
  private getCurrentPath(): string {
    const browserPath = this.document?.location?.pathname || '';
    return browserPath || (this.router.url || '').split('?')[0];
  }

  /** True when current route is an auction details route. */
  public get isAuctionSection(): boolean {
    return /^\/auctions\/.+/.test(this.getCurrentPath());
  }

  /** True when current route is the user profile view. */
  public get isProfileSection(): boolean {
    return /^\/user\/profile(?:\/invoices|\/password)?\/?$/.test(this.getCurrentPath());
  }

  /** True when the Rasmus TV campaign page is active. */
  public get isRasmusTvSection(): boolean {
    return /^\/rasmus-tv\/?$/.test(this.getCurrentPath());
  }

  constructor(
    public loadingService: LoadingService,
    private cookieService: CookieService,
    private firebaseService: FirebaseService,
    private pathService: PathService,
    private router: Router,
    private notificationService: NotificationService,
    private registrationIntentService: RegistrationIntentService,
    private userService: UserService,
    private auctionService: AuctionService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.userData = {
      dialogRef: null,
    };
    void this.loadHtml();
    const queryParamsSignal = toSignal(this.activatedRoute.queryParams, {
      initialValue: this.activatedRoute.snapshot.queryParams ?? {},
    });
    const routerEventSignal = toSignal(this.router.events, {
      initialValue: null as any,
    });
    const whenReady = <T>(handler: (value: T) => void) => (value: T) => {
      if (!this.signalEventEffectsReady()) {
        return;
      }
      handler(value);
    };
    consumeSignalEvents(this.auctionService.headerCategoryOptions, whenReady((options) => this.syncCategoryOptions(options ?? [])));
    consumeSignalEvents(this.loadingService.openLoginDialog, whenReady(() => this.openLoginDialog()));
    consumeSignalEvents(this.loadingService.loading, whenReady((loading) => this.scheduleLoadingState(loading)));
    consumeSignalEvents(this.auctionService.searchTerm, whenReady((term) => (this.headerSearchTerm = term ?? '')));
    effect(() => {
      if (!this.authResolved()) {
        return;
      }
      if (!this.isAuthenticated()) {
        this.syncedHeaderUserUid = '';
        this.firstName.set('Profile');
        this.showBrowserNotificationAd = false;
        return;
      }

      const uid = this.authService.uid();
      if (uid && uid === this.syncedHeaderUserUid) {
        return;
      }
      this.syncedHeaderUserUid = uid;
      void this.syncHeaderUserName().then(() => this.getBrowserNotificationPermission());
    });
    effect(() => {
      if (!this.signalEventEffectsReady()) {
        return;
      }
      this.syncQueryParams(queryParamsSignal());
    });
    effect(() => {
      if (!this.signalEventEffectsReady()) {
        return;
      }
      const event = routerEventSignal();
      if (event) {
        this.handleRouterEvent(event);
      }
    });
  }

  async ngOnInit() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.hostname = this.document?.location?.hostname;

    // Initialize pages with sid
    if (isDevMode()) {
      this.createPage = `http://localhost:4400/create/${this.sid}`;
      this.loginPage = `http://localhost:4400/login/${this.sid}`;
    } else {
      this.createPage = `https://login.auction-engine.com/create/${this.sid}`;
      this.loginPage = `https://login.auction-engine.com/login/${this.sid}`;
    }

    // Initialize showHome
    if (this.hostname !== 'ararity.auction-engine.com'){
      this.showHome = new RegExp(this.showHomeSites.join('|')).test(this.sid);
    }

    this.subscribeToListners();
  }

  public ngOnDestroy(): void {
    this.signalEventEffectsReady.set(false);
    this.loadingUpdateToken++;
  }

  /**
   * Opens the login dialog without navigating away from the current page.
   * Preserves current path/query/hash and forwards UTM params for tracking.
   */
  public openLoginDialog(): void {
    const pathname = this.document?.location?.pathname || '/';
    const search = this.document?.location?.search || '';
    const hash = this.document?.location?.hash || '';
    const target = `${pathname}${search}${hash}`;
    this.loginDialogPathname = target;
    this.loginDialogVisible = true;
  }

  public handleLoginDialogClosed(): void {
    this.loginDialogVisible = false;
  }

  public handleLoginDialogHide(): void {
    this.loginDialogVisible = false;
    this.loginDialogPathname = null;
  }

  public openRegisterPage() {
    const queryParams: Record<string, string> = {};
    if (this.utmParams['utm_source']) {
      queryParams['utm_source'] = this.utmParams['utm_source'];
    }
    if (this.utmParams['utm_medium']) {
      queryParams['utm_medium'] = this.utmParams['utm_medium'];
    }
    if (this.utmParams['utm_campaign']) {
      queryParams['utm_campaign'] = this.utmParams['utm_campaign'];
    }
    this.router.navigate(['/user/register'], { queryParams });
  }

  public changePassword(): void {
    void this.router.navigate(['/user/profile/password']);
  }

  private async loadHtml(): Promise<void> {
    const html = await this.auctionService.getHtml(this.document.location.href);
    this.header = html.header;
    this.loadingService.footer.emit(html.footer);
    this.cdr.markForCheck();
  }

  public openReminderDialog(): void {
    this.auctionService.action.emit(IAction.OPEN_REMINDER_DIALOG);
  }

  public print(): void {
    this.auctionService.action.emit(IAction.PRINT);
  }

  public scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  public logout(): void {
    this.userService.logout();
    this.registrationIntentService.clearAll();
    this.notificationService.clearAuthBoundReminders();
    this.loginDialogVisible = false;
    this.loginDialogPathname = null;
    this.router.navigateByUrl('/');
    this.notificationService.toast('Logged out');
  }

  /** Trigger auction and item search when the header form is submitted. */
  public handleHeaderSearch(term: string): void {
    const query = term?.trim() ?? '';
    this.headerSearchTerm = query;
    const currentPath = (this.router.url || '').split('?')[0];
    if (query) {
      this.router.navigate(['/search'], { queryParams: { [QUERY_PARAMS.SEARCH]: query }, queryParamsHandling: 'merge' });
    } else {
      const target = currentPath === '/search' ? ['/search'] : ['/'];
      this.router.navigate(target, { queryParams: { [QUERY_PARAMS.SEARCH]: null }, queryParamsHandling: 'merge' });
    }
  }

  /** User declines to enable browser notifications. Set cookie so we stop asking. */
  public declineBrowserNotification(): void {
    try {
      this.cookieService.setCookie('decline_notification', true);
      this.showBrowserNotificationAd = false;
      this.notificationService.toast("No problem! Browser notifications will stay off.", 'success');
    } catch {
      this.notificationService.toast("We couldn't save your preference. Please try again.", 'error');
    }
  }

  /** Enable browser notifications. */
  public async enableBrowserNotification(): Promise<void> {
    if (!this.browserNotificationsSupported()) {
      this.showBrowserNotificationAd = false;
      this.notificationService.toast("Browser notifications aren't supported in this browser.", 'error');
      return;
    }

    try {
      const permission = await window.Notification.requestPermission();
      this.showBrowserNotificationAd = false;
      this.cdr.markForCheck();

      if (permission === 'granted') {
        this.notificationService.toast("You're all set! Browser notifications are now enabled.", 'success');
        return;
      }

      this.notificationService.toast(
        "Browser notifications weren't enabled. You can update this anytime in your browser settings.",
        'error',
      );
    } catch {
      this.notificationService.toast("We couldn't update browser notifications. Please try again.", 'error');
    }
  }

  /**
   * Handles category selection from the subheader dropdown.
   * Navigates to the auctions page if needed and emits the selection.
   * @param category - Selected category value or null when cleared
   */
  public onCategoryChange(category: string | null): void {
    const emitSelection = () => this.auctionService.headerCategorySelection.emit(category);
    const currentPath = (this.router.url || '').split('?')[0];
    const target = category ? ['/search'] : [currentPath === '/search' ? '/search' : '/'];
    this.router
      .navigate(target, { queryParams: { [QUERY_PARAMS.CATEGORY]: category }, queryParamsHandling: 'merge' })
      .then(() => emitSelection());
    /**
     * Clear the selected category after emission to reset dropdown display.
     * Deferred to allow PrimeNG to complete its internal change detection cycle
     * and prevent visual flicker when the selection changes.
     */
    setTimeout(() => {
      this.selectedCategory = null;
      this.cdr.markForCheck();
    });
  }

  /**
   * Syncs header category options and clears invalid selections.
   * @param options - Latest category options from auctions data
   */
  private syncCategoryOptions(options: Array<{ label: string; value: string }>): void {
    this.categoryOptions = options;
    if (this.selectedCategory && !this.categoryOptions.some((opt) => opt.value === this.selectedCategory)) {
      this.selectedCategory = null;
    }
  }

  private async syncHeaderUserName(): Promise<void> {
    await this.authService.whenResolved();
    const authUser = this.authService.user();
    const firstName = authUser?.displayName?.split(' ')?.[0]?.trim();
    this.firstName.set(firstName || 'Profile');
  }

  /** Show browser notification prompt to signed-in users unless they declined. */
  private getBrowserNotificationPermission(): void {
    if (!this.authService.isAuthenticated()) {
      this.showBrowserNotificationAd = false;
      return;
    }

    const declined = this.cookieService.getCookie('decline_notification');
    const unsupported = !this.browserNotificationsSupported();
    if (unsupported) {
      return;
    }
    const alreadyGranted = window.Notification.permission === 'granted';
    const blocked = window.Notification.permission === 'denied';
    if (declined || blocked || alreadyGranted) {
      return;
    }
    this.showBrowserNotificationAd = true;
    this.cdr.markForCheck();
  }

  private browserNotificationsSupported(): boolean {
    return 'Notification' in window && typeof window.Notification?.requestPermission === 'function';
  }

  /** Subscribe to listeners. */
  private subscribeToListners() {
    this.signalEventEffectsReady.set(true);
  }

  private scheduleLoadingState(loading: boolean): void {
    const token = ++this.loadingUpdateToken;
    setTimeout(() => {
      if (token !== this.loadingUpdateToken) {
        return;
      }
      this.loading = loading;
      if (!this.loaded) {
        this.loaded = true;
      }
      this.cdr.markForCheck();
    });
  }

  private syncQueryParams(params: Record<string, string>): void {
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign'];
    utmKeys.forEach((key) => {
      if (params[key]) {
        this.utmParams[key] = params[key];
      }
    });

    if (this.utmParams['utm_source']) {
      const utmQuery = `utm_source=${this.utmParams['utm_source']}&utm_medium=${this.utmParams['utm_medium']}&utm_campaign=${this.utmParams['utm_campaign']}`;
      this.createPage += this.createPage.includes('?') ? `&${utmQuery}` : `?${utmQuery}`;
      this.loginPageUtms = `&${utmQuery}`;
    }
    this.headerSearchTerm = params[QUERY_PARAMS.SEARCH] ?? '';
  }

  private handleRouterEvent(event: any): void {
    if (event instanceof NavigationStart) {
      this.loadingRoute = true;
      this.loadingService.routes.push(event.url);
    } else if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      this.loadingRoute = false;
      this.loadingService.routes.push(event.url);
    }
    /** Only keep 5 routes. */
    if (this.loadingService.routes.length > 5) {
      this.loadingService.routes.shift();
    }
  }

}
